import dayjs, { type Dayjs } from 'dayjs';

import { EASTERN_TZ } from './eastern';

/**
 * The Date Range options shared by Show History (US-101880) and View Alerts (US-113523).
 *
 * Both stories specify the same four options, the same "Last 7 Days" default and the same
 * custom-range rules, so they share one type here rather than drifting apart in two features.
 * This lives in `lib/date` because ADR-0007 keeps date arithmetic out of feature code, and
 * `lib` is the narrowest scope both consumers share (ADR-0009 § constants placement).
 */
export const EVENT_DATE_RANGE = {
  TODAY: 'today',
  LAST_7_DAYS: 'last7Days',
  LAST_30_DAYS: 'last30Days',
  CUSTOM: 'custom',
} as const;

export type EventDateRange = (typeof EVENT_DATE_RANGE)[keyof typeof EVENT_DATE_RANGE];

/** Option copy, verbatim from both stories' Section C. */
export const EVENT_DATE_RANGE_LABELS: Record<EventDateRange, string> = {
  [EVENT_DATE_RANGE.TODAY]: 'Today',
  [EVENT_DATE_RANGE.LAST_7_DAYS]: 'Last 7 Days',
  [EVENT_DATE_RANGE.LAST_30_DAYS]: 'Last 30 Days',
  [EVENT_DATE_RANGE.CUSTOM]: 'Custom',
};

/** Render order of the options in the dropdown. */
export const EVENT_DATE_RANGES: readonly EventDateRange[] = [
  EVENT_DATE_RANGE.TODAY,
  EVENT_DATE_RANGE.LAST_7_DAYS,
  EVENT_DATE_RANGE.LAST_30_DAYS,
  EVENT_DATE_RANGE.CUSTOM,
];

/** "Last 7 Days" — the landing default both stories name explicitly, and what Clear returns to. */
export const DEFAULT_EVENT_DATE_RANGE: EventDateRange = EVENT_DATE_RANGE.LAST_7_DAYS;

/** A picked custom window. Both ends are `YYYY-MM-DD`; either may be missing while picking. */
export type CustomDateRange = { start: string | null; end: string | null };

export const EMPTY_CUSTOM_RANGE: CustomDateRange = { start: null, end: null };

/**
 * A resolved, inclusive day span. Both endpoints take dates, not instants, so this is
 * deliberately day-grained and formatted as the `YYYY-MM-DD` the history endpoint documents.
 */
export type EventDateSpan = { startDate: string; endDate: string };

const API_FORMAT = 'YYYY-MM-DD';

/** Today in **ET**, not in the viewer's zone — the rows are ET, so the window must be too. */
function easternToday(now?: Dayjs): Dayjs {
  return (now ?? dayjs()).tz(EASTERN_TZ).startOf('day');
}

/**
 * Resolve a range selection into the `startDate`/`endDate` query params.
 *
 * Returns `null` only for an incomplete Custom range — callers treat that as "don't filter by
 * date yet", which cannot reach the wire because Apply stays disabled in that state.
 */
export function resolveEventDateSpan(
  range: EventDateRange,
  custom: CustomDateRange = EMPTY_CUSTOM_RANGE,
  now?: Dayjs,
): EventDateSpan | null {
  const today = easternToday(now);

  switch (range) {
    case EVENT_DATE_RANGE.TODAY:
      return { startDate: today.format(API_FORMAT), endDate: today.format(API_FORMAT) };
    // "including the current date" (both stories' default-filter scenario) — so six days back
    // plus today, not seven days back.
    case EVENT_DATE_RANGE.LAST_7_DAYS:
      return {
        startDate: today.subtract(6, 'day').format(API_FORMAT),
        endDate: today.format(API_FORMAT),
      };
    case EVENT_DATE_RANGE.LAST_30_DAYS:
      return {
        startDate: today.subtract(29, 'day').format(API_FORMAT),
        endDate: today.format(API_FORMAT),
      };
    case EVENT_DATE_RANGE.CUSTOM:
      if (!custom.start || !custom.end) return null;
      return { startDate: custom.start, endDate: custom.end };
  }
}

/** A custom range is usable only once both ends are picked and ordered. Apply gates on this. */
export function isEventRangeApplicable(range: EventDateRange, custom: CustomDateRange): boolean {
  if (range !== EVENT_DATE_RANGE.CUSTOM) return true;
  const { start, end } = custom;
  return Boolean(start && end && start <= end);
}

/**
 * The applied-filter summary line both panels render on the trigger: the date range, spelled out
 * as the picked days once Custom is complete.
 */
export function formatEventRangeLabel(
  range: EventDateRange,
  custom: CustomDateRange = EMPTY_CUSTOM_RANGE,
): string {
  if (range !== EVENT_DATE_RANGE.CUSTOM) return EVENT_DATE_RANGE_LABELS[range];
  if (!custom.start || !custom.end) return EVENT_DATE_RANGE_LABELS[EVENT_DATE_RANGE.CUSTOM];
  const format = (value: string) => dayjs(value, API_FORMAT).format('MMM D, YYYY');
  return `${format(custom.start)} - ${format(custom.end)}`;
}

/** Today in ET as `YYYY-MM-DD` — the picker's `maxDate`, since future dates are disabled. */
export function easternTodayIso(now?: Dayjs): string {
  return easternToday(now).format(API_FORMAT);
}
